import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, type LucideIcon } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import EmptyState from './EmptyState';
import { cn } from '@/lib/utils';

export interface ColumnDef<T extends object> {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (row: T) => React.ReactNode;
}

export interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

interface DataTableProps<T extends object> {
  columns: ColumnDef<T>[];
  data: T[];
  loading?: boolean;
  emptyIcon?: LucideIcon;
  emptyTitle?: string;
  emptyDescription?: string;
  onRowClick?: (row: T) => void;
  pagination?: PaginationProps;
  className?: string;
}

type SortDir = 'asc' | 'desc';

function DataTable<T extends object>({
  columns, data, loading, emptyIcon, emptyTitle = 'No data', emptyDescription,
  onRowClick, pagination, className,
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sorted = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      const ra = a as Record<string, unknown>, rb = b as Record<string, unknown>;
      const av = ra[sortKey], bv = rb[sortKey];
      const cmp = String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortKey, sortDir]);

  const skeletonRows = 5;
  const totalPages = pagination ? Math.ceil(pagination.total / pagination.pageSize) : 1;

  const SortIcon = ({ col }: { col: ColumnDef<T> }) => {
    if (!col.sortable) return null;
    if (sortKey !== col.key) return <ChevronsUpDown size={12} className="ml-1 text-slate-400" />;
    return sortDir === 'asc'
      ? <ChevronUp size={12} className="ml-1 text-slate-600" />
      : <ChevronDown size={12} className="ml-1 text-slate-600" />;
  };

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="rounded-md border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              {columns.map(col => (
                <TableHead
                  key={col.key}
                  className={cn('text-xs font-medium text-slate-500 h-8 py-0', col.sortable && 'cursor-pointer select-none hover:text-slate-700')}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                >
                  <span className="inline-flex items-center">
                    {col.label}<SortIcon col={col} />
                  </span>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: skeletonRows }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map(col => (
                    <TableCell key={col.key} className="py-2">
                      <Skeleton className="h-4 w-full max-w-[120px]" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="p-0">
                  {emptyIcon
                    ? <EmptyState icon={emptyIcon} title={emptyTitle} description={emptyDescription} />
                    : <div className="py-10 text-center text-sm text-slate-400">{emptyTitle}</div>
                  }
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((row, i) => (
                <TableRow
                  key={i}
                  className={cn(onRowClick && 'cursor-pointer hover:bg-slate-50')}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map(col => (
                    <TableCell key={col.key} className="py-2 text-sm text-slate-700">
                      {col.render ? col.render(row) : String((row as Record<string, unknown>)[col.key] ?? '')}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {pagination && totalPages > 1 && (
        <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
          <span>{pagination.total} item{pagination.total !== 1 ? 's' : ''}</span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline" size="icon"
              className="h-6 w-6"
              disabled={pagination.page <= 1}
              onClick={() => pagination.onPageChange(pagination.page - 1)}
            >
              <ChevronLeft size={12} />
            </Button>
            <span className="px-1">{pagination.page} / {totalPages}</span>
            <Button
              variant="outline" size="icon"
              className="h-6 w-6"
              disabled={pagination.page >= totalPages}
              onClick={() => pagination.onPageChange(pagination.page + 1)}
            >
              <ChevronRight size={12} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
